import React from 'react';
import styled, { css } from '@xstyled/styled-components';
import Icon from '~/assets/icons/new_chevron.svg';
import IconX from '~/assets/icons/close.svg';
import { t } from 'ttag';

const ArrowIcon = styled(Icon)`
  transform: rotate(180deg);
  fill: black;
`;

const CloseIcon = styled(IconX)`
  transform: rotate(180deg);
  fill: black;
`;

/**
 * Opaque bar pinned to the top of the modal scrollport, so scrolled content
 * passes under it instead of under a bare icon (CAT-1252). The modal's
 * rounded clip trims its corners.
 */
const StickyBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  width: 100%;
  box-sizing: border-box;
  padding: ${p => p.theme.space.lg} ${p => p.theme.space.lg}
    ${p => p.theme.space.sm};
  background: ${p => p.theme.colors.white};
`;

const Button = styled.button<{ $sticky: boolean }>`
  width: fit-content;
  height: fit-content;
  display: flex;
  z-index: 1;
  border: none;
  padding: 5px;
  background: transparent;
  border-radius: 4px;

  ${p =>
    !p.$sticky &&
    css`
      position: absolute;
      top: ${p.theme.space.lg};
      left: ${p.theme.space.lg};
    `}

  &:focus,
  &:focus-visible {
    outline: 2px solid var(--color-focus-ring);
    outline-offset: 2px;
  }
`;

export const CloseButton = ({
  onClick,
  ariaLabel,
  type = 'arrow',
  autoFocus = true,
  disabled = false,
  /**
   * Pin to the top of the nearest scrollport (Help / FAQ / Thumbs). Only for
   * containers that scroll and wrap the whole body: the bar is in-flow, so
   * elsewhere it just adds a strip outside the card (CAT-1632).
   */
  sticky = false
}: {
  onClick: () => void;
  type?: string;
  ariaLabel?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  sticky?: boolean;
}) => {
  const button = (
    <Button
      $sticky={sticky}
      onClick={onClick}
      aria-label={ariaLabel || t`close`}
      tabIndex={0}
      className="closeBtn"
      role="button"
      autoFocus={autoFocus}
      disabled={disabled}
    >
      {type === 'arrow' ? (
        <ArrowIcon aria-hidden="true" focusable="false" />
      ) : (
        <CloseIcon aria-hidden="true" focusable="false" />
      )}
    </Button>
  );

  return sticky ? <StickyBar>{button}</StickyBar> : button;
};
